class ProductCard extends HTMLElement {
  constructor() {
    super();
    this.handleFormSubmit = this.handleFormSubmit.bind(this);
    this.handleVariantTriggerClick = this.handleVariantTriggerClick.bind(this);
  }

  connectedCallback() {
    if (this._listenerAdded) return;
    this._listenerAdded = true;

    this.forms = Array.from(this.querySelectorAll('form[action]'));
    this.variantSelect = this.querySelector('[data-card4-variant-select]');
    this.variantTriggers = Array.from(this.querySelectorAll('[data-card4-variant-trigger]'));
    this.moreLink = this.querySelector('[data-card4-more-link]');
    this.priceElement = this.querySelector('[data-card4-price]');
    this.compareElement = this.querySelector('[data-card4-compare]');
    this.compareValueElement = this.querySelector('[data-card4-compare-value]');

    this.forms.forEach((form) => {
      form.addEventListener('submit', this.handleFormSubmit);
    });

    this.variantTriggers.forEach((trigger) => {
      trigger.addEventListener('click', this.handleVariantTriggerClick);
    });

    if (this.variantSelect) {
      const initialVariant = this.getSelectedVariantOption();
      if (initialVariant) {
        this.syncVariantState(initialVariant);
      }
    }
  }

  disconnectedCallback() {
    if (!this._listenerAdded) return;

    this.forms?.forEach((form) => {
      form.removeEventListener('submit', this.handleFormSubmit);
    });

    this.variantTriggers?.forEach((trigger) => {
      trigger.removeEventListener('click', this.handleVariantTriggerClick);
    });

    this._listenerAdded = false;
  }

  getSelectedVariantOption() {
    if (!this.variantSelect) return null;

    return (
      this.variantSelect.selectedOptions?.[0] ||
      this.variantSelect.querySelector('option[selected]') ||
      this.variantSelect.options?.[0] ||
      null
    );
  }

  handleVariantTriggerClick(event) {
    event.preventDefault();
    const trigger = event.currentTarget;
    if (trigger?.disabled) return;
    this.selectVariantById(trigger.dataset.variantId);
  }

  selectVariantById(variantId) {
    if (!this.variantSelect || !variantId) return;

    const variantOption = Array.from(this.variantSelect.options).find((option) => {
      return option.value === variantId || option.dataset.variantId === variantId;
    });

    if (!variantOption || variantOption.disabled) return;

    this.variantSelect.value = variantOption.value;
    variantOption.selected = true;
    this.syncVariantState(variantOption);
  }

  syncVariantState(variantOption) {
    this.updatePrice(variantOption);
    this.updateForms(variantOption);
    this.updateVariantButtons(variantOption);
  }

  updatePrice(variantOption) {
    if (!this.priceElement) return;

    const price = Number.parseInt(variantOption.dataset.variantPrice, 10) || 0;
    const comparePrice = Number.parseInt(variantOption.dataset.variantComparePrice, 10);
    const priceFormatted =
      variantOption.dataset.variantPriceFormatted || this.formatMoney(price);
    const compareFormatted =
      variantOption.dataset.variantComparePriceFormatted || this.formatMoney(comparePrice);
    const hasComparePrice = Number.isFinite(comparePrice) && comparePrice > price;

    this.priceElement.textContent = priceFormatted;

    if (!this.compareElement) return;

    this.compareElement.hidden = !hasComparePrice;
    if (hasComparePrice && this.compareValueElement) {
      this.compareValueElement.textContent = compareFormatted;
    }
  }

  updateForms(variantOption) {
    const variantId = variantOption.dataset.variantId || variantOption.value;
    const available = variantOption.dataset.variantAvailable === 'true';

    this.forms.forEach((form) => {
      const action = form.dataset.productCardAction || 'cart';
      const idInput = form.querySelector('input[name="id"]');
      const submitBtn = form.querySelector('button[type="submit"]');

      if (idInput) {
        idInput.value = variantId;
      }

      form.dataset.variantId = variantId;
      form.dataset.inventoryQuantity = variantOption.dataset.variantInventory || '0';
      form.dataset.inventoryManagement = variantOption.dataset.variantInventoryManagement || '';
      form.dataset.inventoryPolicy = variantOption.dataset.variantInventoryPolicy || '';
      form.hidden = action === 'checkout' && !available;

      if (!submitBtn) return;

      const text = submitBtn.querySelector('.btn-text');
      const availableText = submitBtn.dataset.availableText || text?.textContent || '';
      const soldOutText = submitBtn.dataset.soldOutText || availableText;

      if (text) {
        text.textContent = available ? availableText : soldOutText;
      }

      submitBtn.disabled = !available;
    });
  }

  updateVariantButtons(variantOption) {
    const selectedId = variantOption.dataset.variantId || variantOption.value;
    let hasVisibleActive = false;

    this.variantTriggers.forEach((trigger) => {
      const isActive = trigger.dataset.variantId === selectedId;
      trigger.classList.toggle('is-active', isActive);
      trigger.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      hasVisibleActive = hasVisibleActive || isActive;
    });

    if (this.moreLink) {
      this.moreLink.classList.toggle('is-active', !hasVisibleActive);
    }
  }

  formatMoney(cents) {
    if (window.Money?.format) {
      return window.Money.format(cents);
    }

    const safeValue = Number.isFinite(cents) ? cents : 0;
    const symbol = window.theme?.currencySymbol || '$';
    return `${symbol}${(safeValue / 100).toFixed(2)}`;
  }

  async handleFormSubmit(event) {
    event.preventDefault();

    const form = event.currentTarget;
    const submitBtn = form.querySelector('button[type="submit"]');

    if (!submitBtn || submitBtn.disabled) return;

    submitBtn.setAttribute('disabled', 'disabled');
    submitBtn.classList.add('loading');

    const formData = new FormData(form);
    const variantData = this.getSelectedVariantOption() || form;
    const action = form.dataset.productCardAction || 'cart';

    try {
      if (action === 'checkout') {
        await this.buyNow(formData, variantData);
        return;
      }

      const cartResponse = await this.submitToCart(formData, variantData);
      const cart = window.CartApi?.refreshCart
        ? await CartApi.refreshCart(cartResponse)
        : cartResponse;

      document.dispatchEvent(new CustomEvent('cart:added', { detail: cart || cartResponse }));

      if (!window.SimpleHeaderCart?.open) {
        const cartTrigger =
          document.getElementById('simple-header-cart-trigger') ||
          document.getElementById('custom-header-cart-trigger');
        if (cartTrigger) {
          cartTrigger.click();
        }
      }
    } catch (error) {
      if (window.CartApi?.showToast) {
        CartApi.showToast(error.message, true);
      } else {
        console.error(error);
      }
    } finally {
      submitBtn.classList.remove('loading');

      const selectedVariant = this.getSelectedVariantOption();
      const isAvailable =
        !selectedVariant || selectedVariant.dataset.variantAvailable === 'true';

      if (isAvailable) {
        submitBtn.removeAttribute('disabled');
      } else {
        submitBtn.setAttribute('disabled', 'disabled');
      }
    }
  }

  async submitToCart(formData, variantData) {
    if (window.CartApi?.addToCart) {
      return CartApi.addToCart(formData, { variantData });
    }

    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: formData
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.description || 'Could not add to cart');
    }

    return response.json();
  }

  async buyNow(formData, variantData) {
    await this.submitToCart(formData, variantData);

    const rootPath = window.Shopify?.routes?.root || '/';
    window.location.assign(new URL('checkout', `${window.location.origin}${rootPath}`).toString());
  }
}

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard);
}
